// "Karmik Enerji" calculator — admin-defined rows evaluated against a birth
// date and a user-chosen target date.
// Tokens: bday, bmonth, byear, tday, tmonth, tyear
// Functions: digits(x) = single-pass digit sum, r22(x) = subtract 22 while > 22
// Rules: 'r22' | 'digits' | 'both' ("r22/digits" when raw > 22, else raw) | 'raw'

export const ENERGY_RULES = [
  { value: 'both', label: 'Hər ikisi (22-dən böyükdürsə a/b)' },
  { value: 'r22', label: '22-qaydası' },
  { value: 'digits', label: 'Rəqəm cəmi' },
  { value: 'raw', label: 'Xam (azaltma yoxdur)' },
];

const kabala = { label: 'Kabala', formula: '(tyear-byear)+tyear', rule: 'digits' };

export const DEFAULT_ENERGY_CALC = {
  enabled: false,
  title: 'Karmik Enerji Hesaplama',
  tabs: [
    { id: 'year', name: 'İl', rows: [
      { id: 'y1', label: 'İlk sonuç', formula: 'digits(bday)+digits(bmonth)+digits(tyear)', rule: 'both' },
      { id: 'y2', label: 'İkinci sonuç', formula: 'r22(bday)+bmonth+digits(tyear)', rule: 'both' },
      { id: 'y3', ...kabala },
    ] },
    { id: 'day', name: 'Gün', rows: [
      { id: 'd1', label: 'İlk sonuç', formula: 'digits(bday)+digits(bmonth)+digits(tday)+digits(tmonth)+digits(tyear)', rule: 'both' },
      { id: 'd2', label: 'İkinci sonuç', formula: 'r22(bday)+bmonth+r22(tday)+tmonth+digits(tyear)', rule: 'both' },
      { id: 'd3', ...kabala },
    ] },
    { id: 'month', name: 'Ay', rows: [
      { id: 'm1', label: 'Doğum ayı daxil · rəqəmlər', formula: 'digits(bday)+digits(bmonth)+digits(tmonth)+digits(tyear)', rule: 'both' },
      { id: 'm2', label: 'Doğum ayı daxil · sayılar', formula: 'bday+bmonth+tmonth+digits(tyear)', rule: 'both' },
      { id: 'm3', label: 'Doğum ayı xaric · rəqəmlər', formula: 'digits(bday)+digits(tmonth)+digits(tyear)', rule: 'both' },
      { id: 'm4', label: 'Doğum ayı xaric · sayılar', formula: 'bday+tmonth+digits(tyear)', rule: 'both' },
    ] },
  ],
};

export function digitSum(n) {
  return String(Math.abs(Math.round(n))).split('').reduce((s, d) => s + parseInt(d, 10), 0);
}

export function r22(n) {
  let v = Math.abs(Math.round(n));
  while (v > 22) v -= 22;
  return v === 0 ? 22 : v;
}

// Display per rule. `arkanRaw` = sum where a day > 22 was reduced by r22,
// `rakamRaw` = sum where that day was reduced by digit-sum (31 → 9 vs 4).
export function applyRule(arkanRaw, rule, rakamRaw = arkanRaw) {
  if (arkanRaw === null || arkanRaw === undefined) return null;
  switch (rule) {
    case 'r22': return String(r22(arkanRaw));
    case 'digits': return String(digitSum(rakamRaw));
    case 'raw': return String(arkanRaw);
    default: {
      const a = arkanRaw > 22 ? r22(arkanRaw) : arkanRaw;
      const b = rakamRaw > 22 ? digitSum(rakamRaw) : rakamRaw;
      return arkanRaw <= 22 && arkanRaw === rakamRaw ? String(a) : `${a} / ${b}`;
    }
  }
}

const DAY_TOKENS = new Set(['bday', 'tday']);

// Substitute tokens for one branch. Explicit r22(x)/digits(x) are resolved
// first so they stay fixed; a bare day token > 22 depends on the branch.
function substitute(formula, vars, branch) {
  let expr = formula;
  const keys = Object.keys(vars).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    const v = vars[k];
    if (typeof v !== 'number') continue;
    expr = expr.split(`r22(${k})`).join(`(${r22(v)})`).split(`digits(${k})`).join(`(${digitSum(v)})`);
    let plain = v;
    if (DAY_TOKENS.has(k) && v > 22) plain = branch === 'arkan' ? r22(v) : digitSum(v);
    expr = expr.split(k).join(`(${plain})`);
  }
  return expr;
}

function evalExpr(expr) {
  const stripped = expr.replace(/digits|r22|abs/gi, '');
  if (!/^[\d+\-*/().\s]*$/.test(stripped)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const raw = new Function('digits', 'r22', 'abs', `"use strict"; return (${expr});`)(digitSum, r22, Math.abs);
    if (typeof raw !== 'number' || !isFinite(raw)) return null;
    return Math.round(raw);
  } catch {
    return null;
  }
}

// Returns { raw, rakamRaw, display } or null when the formula is invalid.
// `formula2` (optional) is a separate Rəqəm-branch formula; when empty the
// Arkan formula is reused with bare day tokens > 22 reduced by digit-sum.
export function evaluateEnergyRow(row, vars) {
  const f = String(row?.formula || '').trim();
  if (!f) return null;
  const f2 = String(row?.formula2 || '').trim();
  const arkanRaw = evalExpr(substitute(f, vars, 'arkan'));
  const rakamRaw = evalExpr(substitute(f2 || f, vars, 'rakam'));
  if (arkanRaw === null || rakamRaw === null) return null;
  return { raw: arkanRaw, rakamRaw, display: applyRule(arkanRaw, row.rule, rakamRaw) };
}

export function buildEnergyVars(birth, target) {
  return {
    bday: birth.day, bmonth: birth.month, byear: birth.year,
    tday: target.day, tmonth: target.month, tyear: target.year,
  };
}

export function parseDMY(str) {
  const m = String(str || '').trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10), month = parseInt(m[2], 10), year = parseInt(m[3], 10);
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  return { day, month, year };
}

export function todayDMY() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
